import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, IconButton, Slide } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProviderStatus } from '../../context/ProviderStatusContext';

const DISMISS_KEY = 'providerStatusDismissed';
const HEIGHT_VAR = '--provider-banner-h';

const readDismissed = () => {
  try { return sessionStorage.getItem(DISMISS_KEY); } catch { return null; }
};
const writeDismissed = (value) => {
  try { sessionStorage.setItem(DISMISS_KEY, value); } catch { /* sin almacenamiento: no recuerda el cierre */ }
};

const setHeightVar = (px) => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(HEIGHT_VAR, `${px}px`);
};

/**
 * Banner global (#1722): solo con `degraded` o `down`. Se cierra, y reaparece si empeora
 * (cambia `status`) o si empieza otra incidencia (cambia `since`).
 *
 * No tapa nada: mide su propia altura y la publica en `--provider-banner-h`; AppShell, la barra
 * lateral y el layout simple la reservan, así que el contenido baja en vez de quedar debajo.
 *
 * `zIndex` 9998: si el backend no responde manda `ConnectionBanner` (9999); ese caso no se
 * solapa, porque sin backend /api/status falla, el estado pasa a `unknown` y este se oculta.
 */
const ProviderStatusBanner = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { status, since, provider } = useProviderStatus();
  const [dismissed, setDismissed] = useState(readDismissed);
  const ref = useRef(null);

  const visible = (status === 'degraded' || status === 'down') && dismissed !== `${since}|${status}`;

  // Reserva el espacio mientras el banner está a la vista, y lo libera al irse o desmontar.
  useEffect(() => {
    const el = ref.current;
    if (!visible || !el) {
      setHeightVar(0);
      return undefined;
    }
    const measure = () => setHeightVar(el.offsetHeight);
    measure();
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(el);
    }
    return () => {
      if (observer) observer.disconnect();
      setHeightVar(0);
    };
  }, [visible, status, pathname]);

  if (!visible) return null;

  const name = provider || 'AWS Bedrock';
  const isDown = status === 'down';
  const text = isDown
    ? t('providerStatus.banner.down', 'Nuestro proveedor de IA ({{provider}}) no está respondiendo. No es un problema de tu cuenta.', { provider: name })
    : t('providerStatus.banner.degraded', 'Nuestro proveedor de IA ({{provider}}) tiene una incidencia: algunas respuestas pueden fallar o tardar más.', { provider: name });

  const onClose = () => {
    const key = `${since}|${status}`;
    writeDismissed(key);
    setDismissed(key);
  };

  return (
    <Slide in direction="down" appear timeout={{ enter: 220 }}>
      <Box
        ref={ref}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9998,
          '@media (prefers-reduced-motion: reduce)': { transition: 'none !important' },
        }}
      >
        <Alert
          // `alert` interrumpe al lector de pantalla solo cuando es una caída; una degradación es `status`.
          role={isDown ? 'alert' : 'status'}
          severity={isDown ? 'error' : 'warning'}
          variant="standard"
          sx={{
            borderRadius: 0,
            boxShadow: 2,
            alignItems: 'center',
            py: { xs: 0.5, sm: 0.75 },
            '& .MuiAlert-message': { flex: 1, minWidth: 0, fontSize: { xs: '0.8125rem', sm: '0.875rem' }, lineHeight: 1.4 },
            '& .MuiAlert-action': { alignSelf: 'center', pt: 0, mr: -0.5 },
          }}
          action={(
            <IconButton
              size="small"
              color="inherit"
              aria-label={t('providerStatus.banner.dismiss', 'Cerrar aviso')}
              onClick={onClose}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        >
          {text}
          {pathname !== '/status' && (
            <>
              {' '}
              <Link to="/status" style={{ color: 'inherit', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {t('providerStatus.banner.link', 'Estado del servicio')}
              </Link>
            </>
          )}
        </Alert>
      </Box>
    </Slide>
  );
};

export default ProviderStatusBanner;
